import {
    Button,
    DataTable,
    DescriptionList,
    Modal,
    LegacyStack,
    Text,
    Banner,
    InlineStack,
    TextField, BlockStack, Checkbox
} from "@shopify/polaris";
import {Redirect} from "@shopify/app-bridge/actions";
import {DatePickerModal, toDate} from "../Dates";
import {useCallback, useEffect, useState} from "react";
import Api from "../../helpers/api";
import {useToast} from "@shopify/app-bridge-react";
import OpfForm from "./OpfForm";
import {ShippingMethodSelector} from "./ShippingMethodSelector";
import {getOrderBadges} from "./helpers";

export default ({
    order = null,
    onClose = () => {},
    onOrderSave = () => {},
    config = {},
    paymentMethods = {},
    setDisplayedOrder = {},
}) => {
    const redirect = Redirect.create(window.app);
    const [loading, setLoading] = useState(false);
    const [packDate, setPackDate] = useState(toDate(order?.pack_date));

    const [orderProcessingForm, setOrderProcessingForm] = useState(order?.order_processing_form);
    const [opfPrefill, setOpfPrefill] = useState(order?.customer?.opf);
    const [orderNotes, setOrderNotes] = useState(order?.additional_data?.note ?? '');
    const [placedBy, setPlacedBy] = useState('');
    const [poNumber, setPoNumber] = useState('');

    const toast = useToast();

    const [errors, setErrors] = useState({});

    const [updateShipping, setUpdateShipping] = useState(false);
    const [shippingMethod, setShippingMethod] = useState(order?.shipping_method);

    const [hasPickReadyTag, setHasPickReadyTag] = useState(order?.has_pick_ready_tag);

    useEffect(() => {
        setOrderProcessingForm(order?.order_processing_form);
        setOpfPrefill(order?.customer?.opf);
        setOrderNotes(order?.additional_data?.note ?? '');
        setShippingMethod(order?.shipping_method ?? null);
        setPackDate(toDate(order?.pack_date));

        (order?.additional_data?.note_attributes ?? []).forEach((attribute) => {
            if (attribute.name === '_placed_by') {
                setPlacedBy(attribute.value);
            }
            if (attribute.name === '_PO_NUMBER') {
                setPoNumber(attribute.value);
            }
        });
        setHasPickReadyTag(order?.has_pick_ready_tag);
    }, [order]);

    const processOrder = useCallback(() => {
        setLoading(true);
        Api
            .processOrder(order.id, {
                pack_date: toDate(packDate, false),
            })
            .then(({data}) => {
                toast.show("Order has been processed");
                onOrderSave();
                setDisplayedOrder(data.data);
            })
            .catch(() => toast.show("Server Error, please try again"))
            .finally(() => setLoading(false));
    }, [order, packDate]);

    const closeModal = () => {
        setPackDate(null);
        onClose();
    }

    const toggleSubmit = useCallback(() => {
        setLoading(true);
        Api
            .toggleOrderSubmit(order.id)
            .then(({data}) => {
                toast.show(order.submitted ? 'Order has been unsubmitted' : 'Order has been submitted');
                onOrderSave();
                setDisplayedOrder(data.data);
                setHasPickReadyTag(data.data?.has_pick_ready_tag);
            })
            .catch(() => toast.show("Server Error, please try again"))
            .finally(() => setLoading(false));
    }, [order]);

    const updateOpf = useCallback(() => {
        setErrors({});
        setLoading(true);

        Api
            .updateOpf(order.id, orderProcessingForm)
            .then(({data}) => {
                toast.show('OPF has been updated');
                onOrderSave();
                setDisplayedOrder(data.data);
            })
            .catch(error => {
                const errors = {};
                const opfErrors = {};

                if ( error.response.status == 422 ) {
                    Object.keys(error.response.data.errors).forEach( key => {
                        if (key.startsWith('order_processing_form.')) {
                            opfErrors[key.replace('order_processing_form.', '')] = error.response.data.errors[key][0];
                        } else {
                            errors[key] = error.response.data.errors[key][0];
                        }
                    })

                    if (Object.keys(opfErrors).length) {
                        errors.order_processing_form = opfErrors;
                    }
                } else {
                    toast.show("Server Error, please try again");
                }
                setErrors(errors);
            })
            .finally(() => setLoading(false));
    }, [order, orderProcessingForm]);

    const updateAdditionalInformation = useCallback(() => {
        setLoading(true);
        Api
            .updateAdditionalInformation(order.id, {
                placed_by: placedBy,
                order_notes: orderNotes,
                po_number: poNumber,
                update_shipping: updateShipping,
                shipping_method: shippingMethod,
            })
            .then(({data}) => {
                toast.show('Additional Information has been updated');
                onOrderSave();
                setDisplayedOrder(data.data);
            })
            .catch(() => toast.show("Server Error, please try again"))
            .finally(() => setLoading(false));
    }, [order, orderNotes, poNumber, placedBy, updateShipping, shippingMethod]);

    return (
        <>
        <style>{`
            /* Lift the order modal clear of Shopify's bottom "Work with Sidekick" widget.
               Desktop only; mobile modals are full-screen and left untouched. */
            @media (min-width: 48.0625em) {
                .Polaris-Modal-Dialog__Container { padding-bottom: 70px; }
                .Polaris-Modal-Dialog__Modal { max-height: calc(100vh - 3.75rem - 70px); }
            }
        `}</style>
        <Modal
            open={!!order}
            title={`Order ${order?.additional_data?.name}`}
            onClose={closeModal}
            primaryAction={{
                content: 'Open in Shopify',
                onAction: () => {
                    redirect.dispatch(Redirect.Action.ADMIN_PATH, {
                        path: `/orders/${order?.additional_data?.id}`,
                        newContext: true,
                    });
                },
            }}
            secondaryActions={[
                {
                    content: "Close",
                    onAction: closeModal,
                }, {
                    content: "Save OPF",
                    onAction: () => updateOpf(),
                    loading: loading,
                    disabled: hasPickReadyTag
                }, {
                    content: !order?.submitted ? "Submit" : "Unsubmit",
                    onAction: toggleSubmit,
                    destructive: order?.submitted,
                    loading: loading,
                }
            ]}
        >
            <Modal.Section>
                { !!order?.merged_into_order && (<Banner tone="warning">
                    This Order has been merged into
                    <Text as={'p'} fontWeight="bold">{order.merged_into_order.order_number}</Text>
                </Banner>)}
                { !!order?.merged_orders && order.merged_orders.length > 0 && (<Banner tone="warning">
                    List of all Orders merged into this Order:
                    <Text as={'div'} fontWeight="bold">{order.merged_orders
                        .map(mergedOrder => mergedOrder.order_number)
                        .join(', ')}</Text>
                </Banner>)}

                {getOrderBadges(order)}

                <DescriptionList items={[
                    {term: "Sales Person", description: order?.sales_rep?.name ?? ''},
                    {
                        term: 'Pack Date',
                        description: <LegacyStack>
                            <DatePickerModal
                                startDate={packDate}
                                modalTitle={"Select Pack Date for Order"}
                                onChange={(date) => setPackDate(toDate(date, false))}
                            />
                            <Button

                                onClick={processOrder}
                                loading={loading}
                                disabled={!packDate || packDate.length == 0}
                                variant="primary">Process Order</Button>
                        </LegacyStack>
                    },
                    {
                        term: "Additional Information",
                        description: <BlockStack gap={'200'}>
                            <TextField
                                autoComplete={'off'}
                                label="Who is the person who is placing this order for the customer?"
                                value={placedBy}
                                onChange={setPlacedBy}
                                disabled={hasPickReadyTag || loading}
                            />

                            <TextField
                                autoComplete={'off'}
                                label="PO Number"
                                value={poNumber}
                                onChange={setPoNumber}
                                disabled={hasPickReadyTag || loading}
                            />

                            <TextField
                                autoComplete={'off'}
                                label="Order Notes"
                                multiline={3}
                                value={orderNotes}
                                onChange={setOrderNotes}
                                disabled={hasPickReadyTag || loading}
                            />

                            <LegacyStack vertical>
                                <Checkbox label={"Update Shipping"} disabled={hasPickReadyTag || loading} checked={updateShipping} onChange={setUpdateShipping} />
                                <ShippingMethodSelector
                                    country={order?.additional_data?.shipping_address?.country_code}
                                    state={order?.additional_data?.shipping_address?.province_code}
                                    shippingMethod={shippingMethod}
                                    setShippingMethod={setShippingMethod}
                                    disabled={!updateShipping}
                                />
                            </LegacyStack>

                            <Button
                                size={'slim'}
                                loading={loading}
                                variant={'primary'}
                                onClick={updateAdditionalInformation}
                                disabled={hasPickReadyTag}
                            >Save Additional Information</Button>
                        </BlockStack>
                    },
                    {
                        term: "Shipping Address",
                        description: <DescriptionList items={[
                            {term: "Name", description: `${order?.additional_data?.shipping_address?.first_name} ${order?.additional_data?.shipping_address?.last_name}`},
                            {term: "Company", description: order?.additional_data?.shipping_address?.company},
                            {term: "Address 1", description: order?.additional_data?.shipping_address?.address1},
                            {term: "Address 2", description: order?.additional_data?.shipping_address?.address2},
                            {term: "City", description: order?.additional_data?.shipping_address?.city},
                            {term: "State", description: order?.additional_data?.shipping_address?.province},
                            {term: "Country", description: order?.additional_data?.shipping_address?.country},
                            {term: "Phone", description: order?.additional_data?.shipping_address?.phone},
                        ]} />
                    },
                    {term: "Shipping Method", description: order?.shipping_method?.name ?? ''},
                    {term: "Shipping Price", description: `$${order?.shipping_method?.price ?? '0.00'}`},
                    {term: "Subtotal", description: `$${order?.additional_data?.subtotal_price ?? '0.00'}`},
                    {term: "Tax", description: `$${order?.additional_data?.total_tax ?? '0.00'}`},
                    {term: "Total Discount", description: `$${order?.additional_data?.total_discounts ?? '0.00'}`},
                    {term: "Total", description: `$${(order?.is_cancelled ? order?.additional_data?.total_price : order?.additional_data?.current_total_price) ?? '0.00'}`},
                ]} />
            </Modal.Section>

            <Modal.Section>
                <Text as={'h1'} variant={"headingLg"}>Line Items</Text>
                <DataTable
                    columnContentTypes={[
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                    ]}
                    headings={[
                        "Product",
                        "Sku",
                        "Price",
                        "Quantity",
                        'Discount',
                        "Total"
                    ]}
                    rows={order?.additional_data?.line_items?.map(function (item) {
                        const quantity = order?.is_cancelled ? item.quantity : item.current_quantity;
                        const totalDiscount = item.discount_allocations?.reduce((total, allocation) => total + parseFloat(allocation.amount),0);
                        const totalPrice = (parseFloat(item.price) * quantity - totalDiscount).toFixed(2);
                        return [
                            item.name,
                            item.sku,
                            `$${item.price}`,
                            quantity,
                            `$${totalDiscount.toFixed(2)}`,
                            `$${totalPrice}`,
                        ];
                    })}
                />
            </Modal.Section>

            <Modal.Section>
                <InlineStack align={'space-between'}>
                    <Text as={'h3'} variant={"headingLg"}>Order Processing Form</Text>
                    <Button size={'slim'} disabled={hasPickReadyTag} loading={loading} variant={'primary'} onClick={() => updateOpf()}>Save OPF</Button>
                </InlineStack>

                <OpfForm
                    form={orderProcessingForm}
                    setForm={setOrderProcessingForm}
                    loading={loading}
                    config={config}
                    errors={errors.order_processing_form ?? {}}
                    opfPrefill={opfPrefill}
                    hasPickReadyTag={hasPickReadyTag}
                    paymentMethods={paymentMethods}
                    customerName={`${order?.additional_data?.customer?.first_name} ${order?.additional_data?.customer?.last_name}`}
                />
            </Modal.Section>
        </Modal>
        </>
    );
}
